// Login screen component with form validation and URL token handling

import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth, useAuthCheck } from '../../hooks/useAuth'
import { authService } from '../../services/authService'
import { getAppConfig } from '../../types/env'
import styles from './LoginScreen.module.css'

interface LoginFormData {
  email: string
  password: string
}

interface LoginError {
  field?: 'email' | 'password' | 'general'
  message: string
}

export const LoginScreen: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  })
  const [error, setError] = useState<LoginError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isTokenLoading, setIsTokenLoading] = useState(false)

  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const config = getAppConfig()

  // Check for URL token on mount
  useAuthCheck()

  useEffect(() => {
    if (isAuthenticated) {
      // Get return URL from state or default to dashboard
      const returnTo = (location.state as any)?.from?.pathname || '/app'
      navigate(returnTo, { replace: true })
    }
  }, [isAuthenticated, navigate, location])

  useEffect(() => {
    // Handle URL token login
    const handleUrlToken = async () => {
      const urlToken = authService.checkUrlToken()
      if (urlToken) {
        setIsTokenLoading(true)
        setError(null)
        
        try {
          // The useAuthCheck hook will handle the actual login
          // We just need to show loading state here
        } catch (err: any) {
          setError({
            field: 'general',
            message: err.response?.data?.detail || 'Invalid login link. Please try again.'
          })
        } finally {
          setIsTokenLoading(false)
        }
      }
    }

    handleUrlToken()
  }, [])

  const validateForm = (data: LoginFormData): LoginError | null => {
    if (!data.email.trim()) {
      return { field: 'email', message: 'Email is required' }
    }

    if (!data.email.includes('@')) {
      return { field: 'email', message: 'Please enter a valid email address' }
    }

    if (!data.password) {
      return { field: 'password', message: 'Password is required' }
    }

    if (data.password.length < 6) {
      return { field: 'password', message: 'Password must be at least 6 characters' }
    }

    return null
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear field-specific errors when user starts typing
    if (error?.field === name) {
      setError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate form data
    const validationError = validateForm(formData)
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)

    try {
      await login(formData.email.trim(), formData.password)
      // Navigation will be handled by useEffect when isAuthenticated becomes true
    } catch (err: any) {
      console.error('Login error:', err)
      
      let errorMessage = 'Login failed. Please try again.'
      
      if (err.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials.'
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.'
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network and try again.'
      }

      setError({ field: 'general', message: errorMessage })
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = () => {
    // TODO: Implement forgot password functionality
    alert('Forgot password functionality will be implemented in a future update.')
  }

  const getInputClassName = (field: 'email' | 'password') => {
    const baseClass = styles.input
    return error?.field === field 
      ? `${baseClass} ${styles.inputError}`
      : baseClass
  }

  if (isTokenLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loginBox}>
          <div className={styles.logoContainer}>
            <img 
              src="/src/assets/clokd-logo.png" 
              alt={config.app.name} 
              className={styles.logo}
              onError={(e) => {
                // Fallback to text if logo fails to load
                e.currentTarget.style.display = 'none'
                const textLogo = document.createElement('h1')
                textLogo.textContent = config.app.shortName
                textLogo.className = styles.textLogo
                e.currentTarget.parentNode?.appendChild(textLogo)
              }}
            />
          </div>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Signing you in...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.loginBox}>
        <div className={styles.logoContainer}>
          <img 
            src="/src/assets/clokd-logo.png" 
            alt={config.app.name} 
            className={styles.logo}
            onError={(e) => {
              // Fallback to text if logo fails to load
              e.currentTarget.style.display = 'none'
              const textLogo = document.createElement('h1')
              textLogo.textContent = config.app.shortName
              textLogo.className = styles.textLogo
              e.currentTarget.parentNode?.appendChild(textLogo)
            }}
          />
        </div>

        <div className={styles.header}>
          <h1 className={styles.title}>Welcome Back</h1>
          <p className={styles.subtitle}>Sign in to your {config.app.shortName} account</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              className={getInputClassName('email')}
              disabled={isLoading}
              autoComplete="email"
              autoFocus
            />
            {error?.field === 'email' && (
              <span className={styles.fieldError}>{error.message}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              className={getInputClassName('password')}
              disabled={isLoading}
              autoComplete="current-password"
            />
            {error?.field === 'password' && (
              <span className={styles.fieldError}>{error.message}</span>
            )}
          </div>

          {error?.field === 'general' && (
            <div className={styles.generalError} role="alert">
              {error.message}
            </div>
          )}

          <button 
            type="submit" 
            disabled={isLoading || !formData.email.trim() || !formData.password}
            className={styles.submitButton}
          >
            {isLoading ? (
              <>
                <span className={styles.buttonSpinner}></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <button 
            type="button"
            onClick={handleForgotPassword}
            className={styles.forgotPasswordButton}
            disabled={isLoading}
          >
            Forgot your password?
          </button>
          
          <div className={styles.appInfo}>
            <span className={styles.version}>v{config.app.version}</span>
          </div>
        </div>
      </div>

      {/* Development mode indicator */}
      {config.debug.auth && (
        <div className={styles.debugInfo}>
          <h4>Debug Info (Dev Mode)</h4>
          <pre>{JSON.stringify({ formData, error, isLoading }, null, 2)}</pre>
        </div>
      )}
    </div>
  )
}